<template>
  <VDropdownWithKeyboardNavigation
    id="customLabel"
    :className="`custom-label is-editing ${isShowLabel ? 'custom-label__show' : ''} ${disabled ? 'pointer-none' : ''}`"
    popperClass="custom-label__dropdown-poper"
    :triggers="[]"
    :distance="4"
    :shown="!!searchLabels.length && isShowLabel"
    :values="searchLabels"
    :selectedElementIndex="selectedElementIndex"
    :isLast="isLast"
    :autoHide="false"
    :disabled="disabled"
    placement="bottom-start"
    @onSelect="onSelectLabel"
    @onScroll="onScroll"
    @selectedIndexUpdate="selectedIndexUpdate"
  >
    <template #label="{ hideDropdown }">
      <button
        v-if="isShowSticker"
        class="custom-label__icon-wrapper"
        @click="onShowLabel"
      >
        <icon
          data="@icon/tag.svg"
          :fill="true"
          width="var(--icon-16)"
          height="var(--icon-16)"
          class="custom-label__icon"
        />
      </button>
      <span class="custom-label__input-wrapper">
        <transition
          @beforeLeave="hideLabel"
          @enter="showLabel"
        >
          <input
            v-if="isShowLabel"
            ref="customLabel"
            :style="{ width: `${inputWidth}px` }"
            :value="localLabel"
            :readonly="disabled"
            :maxlength="LABEL_MAX_LENGTH"
            class="custom-label__input"
            type="search"
            name="item-label-search"
            aria-label="Search labels"
            autocomplete="off"
            placeholder="Add Label"
            @input="onInput($event.target.value)"
            @blur="onBlur"
            @focus="onFocusOnLabel"
            @keydown.enter="onKeyDownEnter(hideDropdown)"
          />
        </transition>
      </span>
    </template>
    <template #item="{ value }">
      <BaseSubHeading
        size="medium"
        weight="400"
        class="is-ellipsis"
      >
        {{ value }}
      </BaseSubHeading>
    </template>
    <template #after-list>
      <BaseSubHeading
        size="extra-small"
        weight="400"
        color="monochrome-05"
        class="custom-label__number-of-elements"
      >
        {{ currentLabelsCount }} from {{ totalLabelsCount }}
      </BaseSubHeading>
    </template>
  </VDropdownWithKeyboardNavigation>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component"
import { Emit, Prop, Watch } from "vue-property-decorator"
import { Action } from "s-vuex-class"

import VDropdownWithKeyboardNavigation from "@/components/Dropdown/VDropdownWithKeyboardNavigation.vue"
import Observer from "@/components/Observer/index.vue"

import Debounce from "@/decorators/Debounce"
import { LABEL_MAX_LENGTH } from "@/helpers/validationHelper"
import { LabelSearchFilter, ResourceSearchTypes, ServerResponse } from "@/models"
import { Actions } from "@/store"
import { elementExpansion } from "@/utils"

const components = {
  Observer,
  VDropdownWithKeyboardNavigation
}

@Options({
  name: "CustomLabel",
  components
})
export default class CustomLabel extends Vue {
  @Prop({ default: "" }) readonly label: string
  @Prop({ default: false }) readonly disabled: boolean
  @Prop() readonly isAddLabelVisible: boolean
  @Prop({ default: "ITEM" }) readonly searchType: ResourceSearchTypes

  LABEL_MAX_LENGTH = LABEL_MAX_LENGTH

  @Action(Actions.SEARCH_LABELS) getCustomLabels: (
    payload: LabelSearchFilter
  ) => Promise<ServerResponse<string> | false>

  searchLabels: string[] = []
  contenteditable = false
  isShowLabel = false
  filter = new LabelSearchFilter({
    q: "",
    type: "ITEM"
  })
  isLast = false
  isShowSticker = true
  totalLabelsCount = 0
  localLabel = ""
  selectedElementIndex = -1
  inputWidth = 0

  minInputWidth = 64

  elementInputRef: HTMLInputElement | undefined

  // Method to calculate input width depending on the content
  calculateInputWidth() {
    this.elementInputRef = this.$refs?.customLabel as HTMLInputElement

    if (!this.elementInputRef) return
    if (!this.localLabel) {
      this.inputWidth = this.minInputWidth
    } else {
      this.elementInputRef.style.width = "0"
      const scrollWidth = this.elementInputRef.scrollWidth + 2
      const elementWidth = this.elementInputRef.offsetWidth
      this.inputWidth = Math.max(scrollWidth, elementWidth)
    }
    this.elementInputRef.style.width = `${this.inputWidth}px`
  }

  created() {
    this.localLabel = this.label
    this.isShowLabel = !!this.localLabel
    this.isShowSticker = !this.localLabel
    this.filter.type = this.searchType
  }

  mounted() {
    this.$nextTick(() => {
      this.calculateInputWidth()
    })
  }

  onKeyDownEnter(hideDropdown) {
    if (this.selectedElementIndex < 0) {
      this.onBlur()
      hideDropdown()
    }
  }

  resetSelectedElementIndex() {
    this.selectedElementIndex = -1
  }

  // This method is needed for scroll to the beginning of the text
  onBlur() {
    if (!this.contenteditable) return
    this.contenteditable = false
    this.isShowLabel = !!this.localLabel
    this.isShowSticker = !this.localLabel
    this.onLabelUpdate()
    this.resetSelectedElementIndex()
    this.calculateInputWidth()
    this.isLast = true
    this.searchLabels = []
    this.elementInputRef?.blur()
  }

  onShowLabel() {
    this.isShowLabel = !this.isShowLabel
  }

  onScroll() {
    if (this.isLast) return
    this.filter.page += 1
    this.onGetCustomLabels(this.localLabel)
  }

  async onGetCustomLabels(value: string) {
    this.filter.q = value
    const searchResult = await this.getCustomLabels(this.filter)

    if (!searchResult || !Array.isArray(searchResult.content)) {
      this.searchLabels = []
      this.isLast = true
      this.totalLabelsCount = 0
      this.resetSelectedElementIndex()
      return
    }

    this.isLast = searchResult.last
    this.totalLabelsCount = searchResult.totalElements

    if (this.filter.page > 1) {
      this.searchLabels = [...this.searchLabels, ...searchResult.content]
    } else {
      this.searchLabels = searchResult.content
    }

    this.resetSelectedElementIndex()
  }

  get currentLabelsCount() {
    return this.searchLabels.length
  }

  @Emit("onAddCustomLabel")
  onFocusOnLabel() {
    if (this.contenteditable || this.disabled) return
    this.contenteditable = true
    this.filter.page = 1
    this.onGetCustomLabels(this.localLabel)
  }

  // These methods are needed to animate the label
  showLabel(el) {
    this.calculateInputWidth()
    this.$nextTick(() => {
      elementExpansion(el, this.inputWidth)
        .play()
        .eventCallback("onComplete", () => {
          el.focus()
        })
    })
  }

  hideLabel(el) {
    elementExpansion(el, this.inputWidth).reverse(0).delay(0.1)
  }

  onSelectLabel({ value }) {
    this.localLabel = value
    this.contenteditable = false
    this.isShowLabel = true
    this.isShowSticker = false
    this.resetSelectedElementIndex()
    this.onLabelUpdate()
    this.$nextTick(() => {
      this.calculateInputWidth()
    })
  }

  onInput(value: string) {
    this.localLabel = value
    this.calculateInputWidth()
    if (!value) this.isShowSticker = true
    this.onGetCustomLabelsWithDebounce(value)
  }

  selectedIndexUpdate(index) {
    this.selectedElementIndex = index
  }

  @Debounce(300)
  onGetCustomLabelsWithDebounce(value) {
    this.filter.page = 1
    this.onGetCustomLabels(value)
  }

  onLabelUpdate() {
    this.localLabel = this.localLabel?.toUpperCase().trim()
    if (this.label === this.localLabel) return
    this.$emit("onLabelUpdate", {
      id: "label",
      value: this.localLabel
    })
  }

  @Watch("isAddLabelVisible")
  trackIsAddLabelVisible() {
    if (!this.isAddLabelVisible && !this.label) {
      this.isShowLabel = false
    } else if (!this.isAddLabelVisible) {
      this.searchLabels = []
    }
  }
}
</script>
